/**
 * Contest — data access (migration 969).
 *
 * A Participant records that they did not agree with a ratified or published
 * Priority List, with a reason. Who agreed is on the list (ratified_by,
 * published_by); who did not is in `priority_set_contests`.
 *
 * Reads: the view `priority_set_contests_v` (one row per contest, with the
 * contester's display name and whether it is open; RLS: workspace members).
 * Writes: the two acts only, `contest_priority_list` and `withdraw_contest`.
 * The table has no client write policy (build contract 2.3c, one write path per
 * act). The RPCs raise on refusal; refusals surface here as { ok: false, error }
 * carrying the database's own sentence, which is already written in user
 * language.
 */
import { getTypedSupabaseClient } from './typedSupabaseClient';
import type { Database } from '../types/database.generated';

export type ContestView = Database['public']['Views']['priority_set_contests_v']['Row'];

export interface ContestResult {
  contest_id: string;
  priority_set_id: string;
  list_state: string;
}

export type WriteResult<T> = { ok: true; data: T } | { ok: false; error: string };

/** The list states a contest may be recorded against (the RPC refuses the rest). */
export const CONTESTABLE_LIST_STATES = ['ratified', 'published'] as const;

export function isContestableListState(state: string | null | undefined): boolean {
  return (CONTESTABLE_LIST_STATES as readonly string[]).includes(state ?? '');
}

function client() {
  return getTypedSupabaseClient();
}

/** Every contest on a list, open and withdrawn, oldest first. */
export async function listContestsForList(prioritySetId: string): Promise<ContestView[]> {
  const { data, error } = await client()
    .from('priority_set_contests_v')
    .select('*')
    .eq('priority_set_id', prioritySetId)
    .order('contested_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as ContestView[];
}

/** The workspace a list belongs to: its own, else its cycle's (the RPC's rule). */
export async function getListWorkspaceId(prioritySetId: string): Promise<string | null> {
  const { data, error } = await client()
    .from('priority_sets')
    .select('workspace_id, cycle_id')
    .eq('id', prioritySetId)
    .maybeSingle();
  if (error || !data) return null;
  if (data.workspace_id) return data.workspace_id;
  const { data: cycle } = await client()
    .from('degap_cycles')
    .select('workspace_id')
    .eq('id', data.cycle_id)
    .maybeSingle();
  return cycle?.workspace_id ?? null;
}

export async function contestPriorityList(prioritySetId: string, reason: string): Promise<WriteResult<ContestResult>> {
  const { data, error } = await client().rpc('contest_priority_list', {
    p_priority_set_id: prioritySetId,
    p_reason: reason,
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true, data: data as unknown as ContestResult };
}

export async function withdrawContest(contestId: string, basis: string): Promise<WriteResult<{ contest_id: string }>> {
  const { data, error } = await client().rpc('withdraw_contest', { p_contest_id: contestId, p_basis: basis });
  if (error) return { ok: false, error: error.message };
  return { ok: true, data: data as unknown as { contest_id: string } };
}

/** "No one has contested this list" / "1 person did not agree" / "3 people did not agree". */
export function didNotAgreeWords(openCount: number): string {
  if (openCount === 0) return 'No one has contested this list';
  return `${openCount} ${openCount === 1 ? 'person' : 'people'} did not agree`;
}
